import React from 'react';
import cn from 'classnames';
import Tooltip from '@dealroadshow/uikit-tooltip';
import EmailAlertsTextTooltipContent from './EmailAlertsTextTooltipContent';
import { EmailAlertsText, DigestEmailAlertsText } from './EmailAlertsText';
import styles from './emailAlertsTextContent.module.scss';
import useResponsive, { QUERIES } from '@dealroadshow/uikit-hooks/useResponsive';
import { ITimezone } from '@framework/timezone/vo/Timezone';
import { IFileUploadEmailAlerts } from '../../domain/vo/FileUploadEmailAlerts';
import { IStagingFileUploadEmailAlerts } from '../../domain/vo/StagingFileUploadEmailAlerts';
import { IDigestEmailAlerts } from '../../domain/vo/DigestEmailAlerts';
import { IAccessRequestEmailAlerts } from '../../domain/vo/AccessRequestEmailAlerts';
import { IQuestionsEmailAlerts } from '../../domain/vo/QuestionsEmailAlerts';

interface IProps {
  wrapperClassName?: string,
  emailAlertsContainerClassName?: string,
  timezone: ITimezone,
  fileUploadAlerts: IFileUploadEmailAlerts,
  stagingFileUploadAlerts: IStagingFileUploadEmailAlerts,
  digestAlerts: IDigestEmailAlerts,
  accessRequestAlerts: IAccessRequestEmailAlerts,
  showAlertsModal: () => void,
  qnaAlerts: IQuestionsEmailAlerts,
  dataTest?: string,
  hideWhenEmpty?: boolean,
}

const isAlertsEnabled = (alertSettings: IFileUploadEmailAlerts |
  IStagingFileUploadEmailAlerts |
  IAccessRequestEmailAlerts |
  IQuestionsEmailAlerts,
) => (
  alertSettings?.realtime?.enabled || alertSettings?.daily?.enabled || alertSettings?.weekly?.enabled
);

const isDigestAlertsEnabled = (alertSettings: IDigestEmailAlerts) => (
  alertSettings?.daily?.enabled || alertSettings?.weekly?.enabled
);

const EmailAlertsTextContent = ({
  wrapperClassName,
  emailAlertsContainerClassName,
  timezone,
  fileUploadAlerts,
  stagingFileUploadAlerts,
  digestAlerts,
  accessRequestAlerts,
  showAlertsModal,
  qnaAlerts,
  dataTest = 'alerts',
  hideWhenEmpty,
}: IProps) => {
  const isMobile = useResponsive(QUERIES.MOBILE_PORTRAIT);

  if (
    hideWhenEmpty &&
    !isAlertsEnabled(fileUploadAlerts) &&
    !isAlertsEnabled(stagingFileUploadAlerts) &&
    !isDigestAlertsEnabled(digestAlerts) &&
    !isAlertsEnabled(accessRequestAlerts) &&
    !isAlertsEnabled(qnaAlerts)
  ) {
    return <div className={ styles.emailAlertsWrp } onClick={ showAlertsModal }>None</div>;
  }

  return (
    <div className={ cn(styles.emailAlertsWrp, wrapperClassName) }>
      <Tooltip
        disabled={ isMobile }
        content={ (
          <EmailAlertsTextTooltipContent
            timezone={ timezone }
            fileUploadAlerts={ fileUploadAlerts }
            stagingFileUploadAlerts={ stagingFileUploadAlerts }
            digestAlerts={ digestAlerts }
            accessRequestAlerts={ accessRequestAlerts }
            qnaAlerts={ qnaAlerts }
          />
        ) }
        containerClassName={ styles.emailAlertsTooltipContainer }
      >
        <span
          className={ cn(styles.emailAlertsContainer, emailAlertsContainerClassName) }
          data-test={ dataTest }
          onClick={ showAlertsModal }
        >
          { (!hideWhenEmpty || isAlertsEnabled(fileUploadAlerts)) && (
            <span className={ styles.rowWrp }>
              <span>File: </span>
              <EmailAlertsText
                alertSettings={ fileUploadAlerts }
                isShort
              />
            </span>
          ) }
          { digestAlerts && (!hideWhenEmpty || isDigestAlertsEnabled(digestAlerts)) && (
            <span className={ styles.rowWrp }>
              <span> Digest: </span>
              <DigestEmailAlertsText
                alertSettings={ digestAlerts }
                isShort
              />
            </span>
          ) }
          { stagingFileUploadAlerts && (!hideWhenEmpty || isAlertsEnabled(stagingFileUploadAlerts)) && (
            <span className={ styles.rowWrp }>
              <span> Staging: </span>
              <EmailAlertsText
                alertSettings={ stagingFileUploadAlerts }
                isShort
              />
            </span>
          ) }
          { accessRequestAlerts && (!hideWhenEmpty || isAlertsEnabled(accessRequestAlerts)) && (
            <span className={ styles.rowWrp }>
              <span> Request: </span>
              <EmailAlertsText
                alertSettings={ accessRequestAlerts }
                isShort
              />
            </span>
          ) }
          { qnaAlerts && (!hideWhenEmpty || isAlertsEnabled(qnaAlerts)) && (
            <span className={ styles.rowWrp }>
              <span> Q&A: </span>
              <EmailAlertsText
                alertSettings={ qnaAlerts }
                isShort
              />
            </span>
          ) }
        </span>
      </Tooltip>
    </div>
  );
};

export default EmailAlertsTextContent;
